import { useEffect, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import Chart from "chart.js/auto";
import { CategoryScale } from "chart.js";
Chart.register(CategoryScale);

export const SectorChart = memo(() => {
  const currentTime = new Date();
  const currentYear = currentTime.getFullYear();
  const currentMonth = currentTime.getUTCMonth() + 1;
  const [reportType, setReportType] = useState("monthly");
  const getCurrentFiscalYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    // Fiscal year starts in September
    const startYear = month >= 9 ? year : year - 1;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
  };
  const [period, setPeriod] = useState(getCurrentFiscalYear());
  const { t } = useTranslation();
  const [sector, setSector] = useState("Education - Secondary Schools");
  const [isLoading, setIsLoading] = useState(true);
  const [sectorOptions, setSectorOptions] = useState([]);
  const [topSellerChartData, setTopSellerChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [sectorsLoaded, setSectorsLoaded] = useState(false);

  const noDataPlugin = {
    id: "noDataPlugin",
    beforeDraw: (chart) => {
      const { datasets } = chart.data;
      const hasData = datasets.some((dataset) => dataset.data.length > 0);
      if (!hasData) {
        const ctx = chart.ctx;
        const { width, height } = chart;
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "16px Arial";
        ctx.fillStyle = "#eb9f9d";
        ctx.fillText(t("no_data_filtered_search_txt"), width / 2, height / 2);
        ctx.restore();
      }
    },
  };

  const sectorTitlePlugin = {
    id: "sectorTitlePlugin",
    layout: {
      padding: {
        top: 100, // or higher if needed
        right: 0,
        bottom: 50,
        left: 0,
      },
    },
    beforeDraw: (chart) => {
      const sector = chart.options.plugins.sectorTitle?.text;
      if (!sector) return;
      const {
        ctx,
        chartArea: { top, left, right },
      } = chart;
      ctx.save();
      ctx.font = "bold 16px Arial";
      ctx.fillStyle = "#333";
      ctx.textAlign = "center";
      ctx.fillText(sector, (left + right) / 2, top - 40); // draw above chart area
      ctx.restore();
    },
  };

  const fiscalQuarters = {
    1: t("quarter_sep_nov"),
    2: t("quarter_dec_feb"),
    3: t("quarter_mar_may"),
    4: t("quarter_jun_aug"),
  };
  const quarterStartMonths = { 1: 9, 2: 12, 3: 3, 4: 6 };

  const buildChartData = (labels,revenue,spendSite,spendCustomer,metaData) => ({
    labels,
    datasets: [
      {
        label: t("total_revenue_gbp_label_txt"),
        data: revenue,
        backgroundColor: "#8dc5e4",
        barThickness: 24,
        meta: metaData.map((d) => ({
          amount: d.amount,
          orderCount: d.orderCount,
          clientCount: d.clientCount,
        })),
      },
      {
        label: t("avg_spend_per_site_txt"),
        data: spendSite,
        backgroundColor: "#70dbd8",
        barThickness: 24,
        meta: metaData.map((d) => ({
          amount: d.amount,
          orderCount: d.orderCount,
        })),
      },
      {
        label: t("avg_spend_per_customer_txt"),
        data: spendCustomer,
        backgroundColor: "#1b81bc",
        barThickness: 24,
        meta: metaData.map((d) => ({
          amount: d.amount,
          clientCount: d.clientCount,
        })),
      },
    ],
  });

  const generateFiscalYearOptions = (currentYear,currentMonth,startYear = 2023,) => {
    const options = [];
    for (let i = currentYear; i >= startYear; i--) {
      if (i === currentYear && currentMonth < 9) continue;
      const label = `${i}-${i + 1}`;
      options.push(
        <option key={i} value={label}>
          {label}
        </option>,
      );
    }
    return options;
  };

  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const response = await http.get(BaseUrl + "admin/account");
        const accountsData = response.data.data;
        const uniqueSectors = [
          ...new Set(
            accountsData.map((acc) => acc.Sectors).filter((s) => s?.trim()),
          ),
        ];
        const defaultSector = "Education - Secondary Schools";
        //default sector at the top of the list
        const orderedSectors = [
          defaultSector,
          ...uniqueSectors.filter((s) => s !== defaultSector),
        ];
        setSectorOptions(orderedSectors);
        // sector state is set to the default if it's valid
        if (!uniqueSectors.includes(sector)) {
          setSector(defaultSector);
        }
        setSectorsLoaded(true);
      } catch (error) {
        console.error("Error fetching sectors:", error);
      }
    };
    fetchSectors();
  }, []);
  // Function to format the date as dd-mm-yyyy
  const formatDate = (date) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    if (!sectorsLoaded) return;
    setIsLoading(true);
    const URL = BaseUrl + "admin/monthlysectorreport";
    http
      .post(URL, { period, reportType })
      .then((response) => {
        setIsLoading(false);
        const rawData = response.data.data;
        const sectorData = rawData.filter((item) => item._id.sector === sector);
        let labels = [],
          revenueData = [],
          spendSiteData = [],
          spendCustomerData = [],
          metaData = [];
        if (reportType === "quarterly") {
          const groupedByQuarter = { 1: [], 2: [], 3: [], 4: [] };
          sectorData.forEach((item) => {
            const quarter = item._id.quarter;
            if (groupedByQuarter[quarter]) {
              groupedByQuarter[quarter].push(item);
            }
          });
          const fiscalYearStart = parseInt(period.split("-")[0]);
          const filteredQuarters = Object.keys(groupedByQuarter).filter(
            (quarterKey) => {
              let startMonth = quarterStartMonths[quarterKey];
              let startYear = fiscalYearStart;
              if (startMonth < 9) startYear += 1;
              return new Date(startYear, startMonth - 1, 1) <= new Date();
            },
          );
          filteredQuarters.forEach((key) => {
            const group = groupedByQuarter[key];
            if (group.length === 0) return;
            const item = group[0];
            labels.push(fiscalQuarters[key]);
            revenueData.push(item.amount);
            spendSiteData.push(
              item.orderCount ? item.amount / item.orderCount : 0,
            );
            spendCustomerData.push(
              item.uniqueClientCount ? item.amount / item.uniqueClientCount : 0,
            );
            metaData.push({
              amount: item.amount,
              orderCount: item.orderCount || 0,
              clientCount: item.uniqueClientCount || 0,
            });
          });
        } else {
          const groupedByDate = {};
          sectorData.forEach((item) => {
            const key =
              reportType === "weekly"
                ? item._id.week
                : reportType === "monthly"
                  ? item._id.month
                  : reportType === "yearly"
                    ? item._id.year
                    : "unknown";
            if (!groupedByDate[key]) groupedByDate[key] = [];
            groupedByDate[key].push(item);
          });
          let sortedKeys = Object.keys(groupedByDate);
          if (reportType === "monthly") {
            const fiscalMonths = [9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8];
            sortedKeys.sort(
              (a, b) => fiscalMonths.indexOf(+a) - fiscalMonths.indexOf(+b),
            );
          } else {
            sortedKeys.sort();
          }
          sortedKeys.forEach((key) => {
            const group = groupedByDate[key];
            if (group.length === 0) return;
            const item = group[0];
            let formattedLabel =
              reportType === "monthly"
                ? new Date(0, +key - 1).toLocaleString("default", {
                    month: "short",
                  })
                : key;
            // Convert the label to dd-mm-yyyy format
            if (!isNaN(new Date(formattedLabel))) {
              formattedLabel = formatDate(formattedLabel);
            }
            labels.push(formattedLabel);
            revenueData.push(item.amount);
            spendSiteData.push(
              item.orderCount ? item.amount / item.orderCount : 0,
            );
            spendCustomerData.push(
              item.uniqueClientCount ? item.amount / item.uniqueClientCount : 0,
            );
            metaData.push({
              amount: item.amount,
              orderCount: item.orderCount || 0,
              clientCount: item.uniqueClientCount || 0,
            });
          });
        }
        const chartData = buildChartData(
          labels,
          revenueData,
          spendSiteData,
          spendCustomerData,
          metaData,
        );
        setTopSellerChartData(chartData);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  }, [reportType, period, sector, sectorsLoaded]);

  return (
    <div
      className={`chart-container chart-revenue ${isLoading ? "loading-buffer" : ""}`}
    >
      <div className="chart-Hdr">
        <h2 style={{ textAlign: "left" }}>
          {t("sector_performance_analysis_txt")}
        </h2>
        <div className="chart-fltr">
          <select
            style={{ width: "" }}
            value={sector}
            onChange={(e) => setSector(e.target.value)}
          >
            {sectorOptions.map((sector, index) => (
              <option key={index} value={sector}>
                {sector}
              </option>
            ))}
          </select>
          <select
            style={{ width: "150px" }}
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
          >
            <option value="weekly">{t("current_week_txt")}</option>
            <option value="monthly">{t("monthly_txt")}</option>
            <option value="quarterly">{t("quarterly_txt")}</option>
            <option value="yearly">{t("fy_data_txt")}</option>
          </select>
          {(reportType === "monthly" ||
            reportType === "quarterly" ||
            reportType === "yearly") && (
            <select
              style={{ width: "170px" }}
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              {generateFiscalYearOptions(currentYear, currentMonth)}
            </select>
          )}
        </div>
      </div>
      {topSellerChartData && (
        <Bar
          data={topSellerChartData}
          plugins={[noDataPlugin, sectorTitlePlugin]}
          options={{
            scales: {
              y: {
                grid: { color: "rgba(247, 243, 241, 0.8)" },
                ticks: { color: "rgba(207, 208, 211, 1)" },
              },
              x: {
                grid: { color: "rgba(247, 243, 241, 0.8)" },
                ticks: {
                  color: "rgba(207, 208, 211, 1)",
                  callback(value) {
                    const label = this.getLabelForValue(value);
                    if (reportType === "weekly" && typeof label === "string" && label.includes(" ")) {
                      const [date, dayOfWeek] = label.split(" ");
                      return [dayOfWeek, date];
                    }
                    return label;
                  },
                },
              },
            },
            layout: { padding: { top: 20, right: 0, bottom: 0, left: 0 } },
            plugins: {
              sectorTitle: {
                text: sector, // this should be a prop or state value
              },
              title: { display: false },
              legend: { display: true },
              tooltip: {
                titleColor: "rgba(220, 222, 226, 1)",
                bodyColor: "rgba(220, 222, 226, 1)",
                callbacks: {
                  label: function (context) {
                    const dataset = context.dataset;
                    const index = context.dataIndex;
                    const value = dataset.data?.[index];
                    const meta = dataset.meta?.[index] || {};
                    const valueStr = value.toString();
                    const valueDecimal = valueStr.split(".")[1];
                    const valueFractionDigits = valueDecimal
                      ? Math.min(valueDecimal.length, 2)
                      : 0;
                    const formattedValue = Number(value).toLocaleString(
                      "en-GB",
                      {
                        minimumFractionDigits: valueFractionDigits,
                        maximumFractionDigits: valueFractionDigits,
                      },
                    );
                    const amount = meta.amount ?? 0;
                    const amountStr = amount.toString();
                    const amountDecimal = amountStr.split(".")[1];
                    const amountFractionDigits = amountDecimal
                      ? Math.min(amountDecimal.length, 2)
                      : 0;
                    const totalRevenue = Number(amount).toLocaleString(
                      "en-GB",
                      {
                        minimumFractionDigits: amountFractionDigits,
                        maximumFractionDigits: amountFractionDigits,
                      },
                    );
                    let lines = [];
                    if (dataset.label === t("total_revenue_gbp_label_txt")) {
                      lines.push(`${dataset.label}: ${formattedValue}`);
                      lines.push(
                        `${t("no_of_orders_txt")}: ${meta.orderCount || 0}`,
                      );
                      lines.push(
                        `${t("no_of_customers_txt")}: ${meta.clientCount || 0}`,
                      );
                    } else if (dataset.label === t("avg_spend_per_site_txt")) {
                      lines.push(`${dataset.label}: ${formattedValue}`);
                      lines.push(
                        `${t("total_revenue_gbp_label_txt")}: ${totalRevenue}`,
                      );
                      lines.push(
                        `${t("no_of_orders_txt")}: ${meta.orderCount || 0}`,
                      );
                    } else if (
                      dataset.label === t("avg_spend_per_customer_txt")
                    ) {
                      lines.push(`${dataset.label}: ${formattedValue}`);
                      lines.push(
                        `${t("total_revenue_gbp_label_txt")}: ${totalRevenue}`,
                      );
                      lines.push(
                        `${t("no_of_customers_txt")}: ${meta.clientCount || 0}`,
                      );
                    }
                    return lines;
                  },
                },
              },
              datalabels: {
                display: false,
                font: { size: 11, weight: "600" },
                padding: 5,
                anchor: "end",
                align: "top",
                formatter: (value) => "£" + value,
              },
            },
          }}
        />
      )}
    </div>
  );
});
